import { TStorageDrawer } from "types";

/**
 * Warnings a supervisor cannot discover anywhere else. The first is the important one: the
 * device cannot hold a drawer with no magnet, so it logs an error and opens it anyway rather
 * than stranding an operator — which means a restriction set here would silently not exist.
 */
export const drawerAccessWarnings = (allDrawers: boolean, allowed: number[], drawers: TStorageDrawer[]): string[] => {
  if (allDrawers) return [];

  const warnings: string[] = [];

  if (allowed.length === 0) {
    warnings.push("This rule allows no drawers. Someone matching it can unlock the toolbox but open nothing.");
    return warnings;
  }

  const unenforceable = drawers
    .filter((drawer) => !allowed.includes(drawer.value))
    .filter((drawer) => drawer.maglockNumber === null || drawer.maglockNumber === undefined)
    .map((drawer) => drawer.value);

  if (unenforceable.length > 0) {
    const list = unenforceable.join(", ");
    warnings.push(
      `No maglock is fitted to drawer ${list}, so it will still open for this rule. Set a maglock number in the drawer configuration to restrict it.`,
    );
  }

  return warnings;
};

/** Compact summary for the access matrix. */
export const summariseDrawerAccess = (allDrawers: boolean, allowed: number[], total: number): string => {
  if (allDrawers) return "All drawers";
  if (allowed.length === 0) return "No drawers";
  return `${allowed.length} of ${total}`;
};

/**
 * Drawer numbers present in `previous` but missing from `next` — i.e. what a drawer edit is
 * about to delete. Compared by drawer `value`, not array position, since storage settings
 * reassigns ids freely when the model changes.
 */
export const findRemovedDrawerValues = (previous: { value: number }[], next: { value: number }[]): number[] => {
  const remaining = new Set(next.map((drawer) => drawer.value));
  return previous.filter((drawer) => !remaining.has(drawer.value)).map((drawer) => drawer.value);
};

/**
 * Names, per removed drawer, how many access rules reference it — the warning shown before the
 * edit that would otherwise silently prune those rules' restrictions server-side.
 */
export const describeDrawerRemovalWarning = (affected: { value: number; count: number }[]): string => {
  return affected
    .map(
      ({ value, count }) =>
        `Removing drawer ${value} will remove it from ${count} access rule${count === 1 ? "" : "s"}.`,
    )
    .join(" ");
};
